import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Avatar, MenuItem, surfaceClass, useDismissable } from './ui'
import { cn } from '@/lib/cn'
import { useSession } from '@/lib/session'
import { useOptionalWorkspace } from '@/lib/workspace'

/** Account menu in the top bar: who am I, account settings, sign out. */
export function UserMenu() {
  const { user, signOut, isPlatformAdmin } = useSession()
  const workspace = useOptionalWorkspace()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useDismissable(open, close)

  if (!user) return null

  async function handleSignOut() {
    setSigningOut(true)
    setOpen(false)
    await signOut()
    // Replace rather than push: the back button must not return to a shell
    // that no longer has a session behind it.
    navigate('/login', { replace: true })
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.name}`}
        className="flex items-center gap-2 rounded-full p-0.5 transition hover:bg-ink-100 dark:hover:bg-ink-800"
      >
        <Avatar name={user.name} />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            surfaceClass,
            'absolute top-full right-0 z-30 mt-1.5 w-60 p-1.5 shadow-lg shadow-ink-950/10',
          )}
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-ink-500 dark:text-ink-400">{user.email}</p>
          </div>
          <div className="my-1 border-t border-ink-200 dark:border-ink-800" />

          {workspace ? (
            <MenuItem
              onClick={() => {
                setOpen(false)
                navigate(workspace.path('/settings/account'))
              }}
            >
              Account settings
            </MenuItem>
          ) : null}
          <MenuItem
            onClick={() => {
              setOpen(false)
              navigate('/invitations')
            }}
          >
            Invitations
          </MenuItem>
          {workspace ? (
            <MenuItem
              onClick={() => {
                setOpen(false)
                navigate(workspace.path('/settings'))
              }}
            >
              Workspace settings
            </MenuItem>
          ) : null}

          {/* The platform admin console. Rendered ONLY for a platform admin —
              not disabled, not greyed out, absent — because an entry point that
              is merely inert still tells everyone the console exists. The API
              answers 404 to anyone without the flag, and this matches it. */}
          {isPlatformAdmin ? (
            <>
              <div className="my-1 border-t border-ink-200 dark:border-ink-800" />
              <MenuItem
                onClick={() => {
                  setOpen(false)
                  navigate('/admin')
                }}
              >
                Platform admin
              </MenuItem>
            </>
          ) : null}

          <div className="my-1 border-t border-ink-200 dark:border-ink-800" />
          <MenuItem danger disabled={signingOut} onClick={() => void handleSignOut()}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </MenuItem>
        </div>
      ) : null}
    </div>
  )
}
